import { useStableCallback } from "hooks"
import { useEffect } from "react"
import { assert, enums, type, type Infer } from "superstruct"
import { getIsUrl } from "../dataUtilities/getIsUrl"

type Message = Infer<typeof IncomingMessage>

const IncomingMessage = type({
  type: enums(["EXIT_MODAL", "LOGOUT"]),
})

export const useIframeMessages = ({
  onMessage,
  targetUrl,
}: {
  onMessage: (message: Message) => void
  targetUrl: string
}) => {
  const stableOnMessage = useStableCallback(onMessage)

  useEffect(() => {
    // Without this check, a "TypeError: URL Constructor: X is not a valid URL"
    // will be thrown. This error does not cause the app to crash but is unnecessary
    // - in majority of cases it happens because the targetURL was not fetched yet.
    const isValidUrl = getIsUrl(targetUrl)
    if (!isValidUrl) return

    const handler = (event: MessageEvent) => {
      messageListener(event, stableOnMessage, targetUrl)
    }

    window.addEventListener("message", handler)
    return () => window.removeEventListener("message", handler)
  }, [stableOnMessage, targetUrl])
}

const messageListener = (
  event: MessageEvent,
  onMessage: (message: Message) => void,
  targetUrl: string
) => {
  if (!isEventValid(event, targetUrl)) return

  const data = parseIncomingMessage(event.data)

  onMessage(data)
}

const isEventValid = (event: MessageEvent, targetUrl: string) => {
  const eventOrigin = event.origin
  const targetOrigin = new URL(targetUrl).origin

  return eventOrigin === targetOrigin
}

const parseIncomingMessage = (data: unknown) => {
  assert(data, IncomingMessage)
  return data
}
